import {observable, action, decorate, runInAction, configure} from 'mobx';
import agent from '../agent';
import AuctionStore from './AuctionStore';
import MessageStore from './MessageStore';
import BidStore from './BidStore';
import {addAuctionToLocalStorage, getSingleToken} from '../shared/utils/localStorage';
import { getApiHost } from '../endpoints';

configure({ enforceActions: 'always' });

class MultiAuctionStore {
    constructor() {
        this.initialize();
        this.usedColors = observable.set();
        this.colorPool = [
            'rgb(172, 0, 0)', // '#1E90FF',
            '#000',
            '#6A5ACD',
            '#7B1FA2',
            '#C2185B',
        ];
    }

    initialize = () => runInAction(() => {
        this.user = {
            account: '',
            accountName: '',
            bidderNumber: '',
            commitments: {},
            ipAddress: null,
            lastUpdated: null,
            member: '',
            memberName: '',
            name: '',
            providerID: null,
            queryString: null,
            sessionToken: null,
            viewOnly: true,
        };

        this.auctions = observable.map([], {deep: true});
        this.bids = observable.map([], {deep: true});
        this.messages = observable.map([], {deep: true});
        this.allAuctionsAndLanes = [];
        this.connected = false;
        this.pingInterval = null;
        this.networkConnected = true;
    });

    // For assigning a unique color to the auction
    assignColor() {
        // Find the first unused color from the pool
        const availableColor = this.colorPool.find((color) => !this.usedColors.has(color));

        // If no colors are available, fallback to default or reuse (optional)
        const assignedColor = availableColor || '#999999'; // Fallback color if all are used

        if (availableColor) {
            this.usedColors.add(assignedColor);
        }

        return assignedColor;
    }

    releaseColor(color) {
        if (this.usedColors.has(color)) {
            this.usedColors.delete(color);
        }
    }


    registerBidder = async ({sessionToken, auctionID, laneID}) => {
        try {
            const host = getApiHost();
            const {Auth} = agent;
            const {data} = await Auth.registerBidder(sessionToken, auctionID, laneID, host);

            const {bidderToken, success, message} = data;
            if (success) {
                return bidderToken
            }
            alert(`Error connecting to auction: ${message}`);
            return null;
        }
        catch (error) {
            console.log(error);
            throw new Error(error);
        }
    }

    // Function to add a new auction store
    addAuction = ({key, auctionID, auctionInfo = {}, providerStore}) => {
        if (!this.auctions.has(key)) {
            const assignedColor = this.assignColor();

            const auctionStore = new AuctionStore(key, {...auctionInfo, color: assignedColor});
            this.auctions.set(key, auctionStore);

            // Create and store a MessageStore for this auction
            const messageStore = new MessageStore(this);
            this.messages.set(key, messageStore);
            // Create and store a BidStore for this auction
            const bidStore = new BidStore(auctionStore, providerStore, messageStore, this);
            this.bids.set(key, bidStore);
            // addAuctionToLocalStorage({key: auctionID, auctionID});
            return auctionStore
        }
        return this.auctions.get(key);
    }

    // Remove an auction and its BidStore
    removeAuction = (auctionID) => {
        if (this.auctions.has(auctionID)) {
            this.auctions.delete(auctionID);
            this.bids.delete(auctionID);
            this.messages.delete(auctionID);
        }
    }

    setProperty = (key, value) => {
        try {
            this[key] = value;
        } catch (error) {
            console.error('setProperty', { error, key, value });
        }
    };

    // Retrieve a specific AuctionStore by ID
    getAuction = (auctionID) => {
        return this.auctions.get(auctionID);
    }

    // Retrieve a specific BidStore by ID
    getBidStore(auctionID) {
        // this.bids.forEach(item => console.log('big store>> item', item))
        return this?.bids?.get(auctionID);
    }

    getMessageStore(auctionID) {
        // this.bids.forEach(item => console.log('big store>> item', item))
        return this?.messages?.get(auctionID);
    }

    setUser = (
        account = '',
        accountName = '',
        member = '',
        memberName = '',
        bidderNumber = '',
        name = '',
        ipAddress = null,
        providerID = null,
        queryString = null,
        viewOnly = true,
        sessionToken = null,
    ) => {
        this.user.account = account;
        this.user.accountName = accountName;
        this.user.member = member;
        this.user.memberName = memberName;
        this.user.bidderNumber = bidderNumber;
        this.user.name = name;
        this.user.viewOnly = viewOnly;
        this.user.ipAddress = ipAddress;
        this.user.providerID = providerID;
        this.user.queryString = queryString;
        this.user.sessionToken = sessionToken;
        this.user.lastUpdated = new Date().toISOString();
    };

    setAllAuctionsAndLanes = (auctionsAndLanes = []) => {
        try {
            this.allAuctionsAndLanes = auctionsAndLanes;
        }
        catch (error) {
            console.error('setAllAuctionsAndLanes error', error);
        }
    }
}

decorate(MultiAuctionStore, {
    auctions: observable,
    bids: observable,
    messages: observable,
    defaultAuctionId: observable,
    pingInterval: observable,
    connected: observable,
    addAuction: action,
    removeAuction: action,
    getAuction: action,
    getBidStore: action,
    getMessageStore: action,
    registerBidder: action,
    setUser: action,
    setAllAuctionsAndLanes: action,
    setProperty: action,
    user: observable,
    allAuctionsAndLanes: observable,
    networkConnected: observable,
});

export default MultiAuctionStore;
